/**
 * One lazily created STOMP client shared by every realtime consumer (today: `useRoomRealtime` and
 * `useOverlayRealtime`; later: WP7's whiteboard). This module owns only the socket's lifecycle, its
 * reconnect-durable subscription registry, and the connection status the room header's indicator
 * reads — it knows nothing about rooms, snapshots, or version gates. That belongs to
 * `useRoomRealtime.ts`/`useOverlayRealtime.ts`.
 *
 * `subscribe()` is reconnect-durable: a destination registered once stays registered across
 * disconnects — every reconnect re-subscribes it automatically (`resubscribeAll`) — so callers
 * never need to notice a drop themselves; `onConnect()` is how a caller finds out a (re)connect
 * just happened, to redo its own fetch-snapshot/hydrate/replay sequence.
 *
 * Headers are per-activation, not per-module: `activate(headers)` recreates the underlying client
 * whenever the requested header set differs from whatever is currently live (e.g. a room page's
 * headerless activation followed by an overlay page's `overlay-token` activation in the same tab —
 * or the reverse). `subscribe()` never constructs a client itself — calling it before any
 * `activate()` just registers the destination; `resubscribeAll` picks it up on the first connect —
 * so a caller can never accidentally "poison" the shared client with the wrong header set by
 * subscribing before activating.
 */
import { Client, type IMessage } from '@stomp/stompjs';
import { create } from 'zustand';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'closed';

interface ConnectionState {
  status: ConnectionStatus;
}

export const useConnectionStore = create<ConnectionState>(() => ({ status: 'closed' }));

function setStatus(status: ConnectionStatus): void {
  useConnectionStore.setState({ status });
}

interface Registration {
  destination: string;
  handler: (body: unknown) => void;
  unsubscribeFn: (() => void) | null;
}

let client: Client | null = null;
/** The header set the live `client` was constructed with, or `null` when there is no live client.
 * Compared against every `activate()` call to decide whether the client must be rebuilt. */
let liveHeaders: Record<string, string> | null = null;
let activeCount = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const registrations = new Set<Registration>();
const connectListeners = new Set<() => void>();

function clearReconnectTimer(): void {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

/** Flips the indicator to "reconnecting" only after 2s without a live connection, so a fast
 * reconnect — the common case — never flashes the coral state. Unconditional inside the timer on
 * purpose: `onConnect` clears it, so if it fires, no connect has happened in those 2s, whatever the
 * store still says. (It used to be guarded by `status !== 'connected'` — which read the *stale*
 * pre-drop status, so a live connection that was lost never flipped, only a never-connected one.
 * In production that hid every reconnect loop: a rotated overlay token, or a restarted api, left
 * the page looking connected while the client retried every 2s behind it.) */
function armReconnectingFlag(): void {
  clearReconnectTimer();
  reconnectTimer = setTimeout(() => {
    setStatus('reconnecting');
  }, 2000);
}

function parseBody(message: IMessage): unknown {
  return message.body ? (JSON.parse(message.body) as unknown) : undefined;
}

function resubscribeAll(stompClient: Client): void {
  for (const registration of registrations) {
    const subscription = stompClient.subscribe(registration.destination, (message) => {
      registration.handler(parseBody(message));
    });
    registration.unsubscribeFn = () => subscription.unsubscribe();
  }
}

function headersEqual(a: Record<string, string>, b: Record<string, string>): boolean {
  const aKeys = Object.keys(a).sort();
  const bKeys = Object.keys(b).sort();
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every((key, index) => key === bKeys[index] && a[key] === b[key]);
}

function createClient(headers: Record<string, string>): Client {
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return new Client({
    brokerURL: `${protocol}://${window.location.host}/api/ws`,
    reconnectDelay: 2000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    ...(Object.keys(headers).length > 0 ? { connectHeaders: headers } : {}),
    onConnect: () => {
      clearReconnectTimer();
      setStatus('connected');
      if (client) {
        resubscribeAll(client);
      }
      for (const listener of connectListeners) {
        listener();
      }
    },
    onWebSocketClose: () => {
      if (activeCount > 0) {
        armReconnectingFlag();
      }
    },
    onStompError: () => {
      if (activeCount > 0) {
        armReconnectingFlag();
      }
    },
  });
}

/** Tears down the live client (if any): deactivates it, drops every registration's live
 * subscription handle (the underlying subscriptions go away with the client itself — there is
 * nothing left to call `.unsubscribe()` on), and nulls the module's client/headers so the next
 * `activate()` builds a fresh one from scratch. */
function teardownClient(): void {
  if (!client) {
    return;
  }
  clearReconnectTimer();
  const closingClient = client;
  client = null;
  liveHeaders = null;
  for (const registration of registrations) {
    registration.unsubscribeFn = null;
  }
  void closingClient.deactivate();
}

/** Activates the shared client (ref-counted, so multiple consumers — e.g. StrictMode's
 * mount/unmount/remount in dev — never fight over one socket) with the given CONNECT headers. If a
 * client is already live with a *different* header set, it is torn down and rebuilt with the new
 * headers — headers are per-activation, never stuck from whichever caller happened to activate
 * first. */
export function activate(connectHeaders?: Record<string, string>): void {
  activeCount += 1;
  const headers = connectHeaders ?? {};
  if (client && liveHeaders && !headersEqual(liveHeaders, headers)) {
    teardownClient();
  }
  if (!client) {
    client = createClient(headers);
    liveHeaders = headers;
  }
  if (!client.active) {
    armReconnectingFlag();
    client.activate();
  }
}

/** Pairs with `activate`; only actually tears the socket down once the last consumer has gone. */
export function deactivate(): void {
  activeCount = Math.max(0, activeCount - 1);
  if (activeCount === 0 && client) {
    teardownClient();
    setStatus('closed');
  }
}

/**
 * Registers `handler` for `destination`, subscribing immediately if a connected client already
 * exists and on every future (re)connect. Safe to call before the first `activate()` — it never
 * constructs a client itself, so it can never lock in the wrong (e.g. headerless) CONNECT; the
 * registration simply sits in `registrations` until a client connects and `resubscribeAll` picks
 * it up. Returns an unsubscribe function.
 */
export function subscribe(destination: string, handler: (body: unknown) => void): () => void {
  const registration: Registration = { destination, handler, unsubscribeFn: null };
  registrations.add(registration);
  if (client?.connected) {
    const subscription = client.subscribe(destination, (message) => handler(parseBody(message)));
    registration.unsubscribeFn = () => subscription.unsubscribe();
  }
  return () => {
    registrations.delete(registration);
    registration.unsubscribeFn?.();
  };
}

/** Fire-and-forget send. Silently does nothing while there is no connected client — the same
 * "just drop it" semantics the server applies to a rate-limited presence message (see WP5 spec).
 * Never constructs a client — publishing before any `activate()` is simply a no-op. Returns
 * whether the frame was actually handed to a connected client: most callers (e.g. presence, which
 * is genuinely best-effort) can ignore it, but a caller that needs to know its data survived a
 * disconnect — `useExcalidrawSync`'s whiteboard deltas — must not treat a silent no-op as "sent". */
export function publish(destination: string, body?: unknown): boolean {
  if (!client?.connected) {
    return false;
  }
  client.publish(body !== undefined ? { destination, body: JSON.stringify(body) } : { destination });
  return true;
}

/** Registers a listener fired on every (re)connect, after subscriptions have been re-established.
 * `useRoomRealtime`/`useOverlayRealtime` use this to redo their fetch-snapshot/hydrate/replay-buffer
 * sequence. */
export function onConnect(listener: () => void): () => void {
  connectListeners.add(listener);
  return () => connectListeners.delete(listener);
}
